 import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { ErrorHandler, NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { MsalBroadcastService, MsalGuard, MsalGuardConfiguration, MsalInterceptor, MsalInterceptorConfiguration, MsalModule, MsalService, MSAL_GUARD_CONFIG, MSAL_INSTANCE, MSAL_INTERCEPTOR_CONFIG, MsalRedirectComponent } from '@azure/msal-angular';
import { BrowserCacheLocation, InteractionType, IPublicClientApplication, LogLevel, PublicClientApplication } from '@azure/msal-browser';
import { NgbCarouselModule, NgbDropdownModule, NgbModule, NgbPaginationModule, NgbToastModule } from '@ng-bootstrap/ng-bootstrap';
import { SlickCarouselModule } from 'ngx-slick-carousel';
import { b2cPolicies } from '../environments/environment';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { AppErrorHandler } from './app.error-handler';
import { BreadcrumbComponent } from './components/breadcrumb/breadcrumb.component';
import { DementiaSeriesComponent } from './components/dementia-series/dementia-series.component';
import { EErgoDashboardComponent } from './components/e-ergo-dashboard/e-ergo-dashboard.component';
import { EErgoHomeComponent } from './components/e-ergo-home/e-ergo-home.component';
import { ManualMaterialHandlingComponent } from './components/e-ergo-templates/manual-material-handling/manual-material-handling.component';
import { OfficeErgonomicsComponent } from './components/e-ergo-templates/office-ergonomics/office-ergonomics.component';
import { EventsComponent } from './components/events/events.component';
import { HomeComponent } from './components/home/home.component';
import { IndustryHighlightsComponent } from './components/industry-highlights/industry-highlights.component';
import { IndustryResourcePageComponent } from './components/industry-resource-page/industry-resource-page.component';
import { MemicMinutesComponent } from './components/memic-minutes/memic-minutes.component';
import { NavMenuComponent } from './components/nav-menu/nav-menu.component';
import { NewsComponent } from './components/news/news.component';
import { PromotedLinksComponent } from './components/promoted-links/promoted-links.component';
import { ResourceLibraryComponent } from './components/resource-library/resource-library.component';
import { ToastsContainer } from './components/toasts/toasts.component';
import { TopResourcesComponent } from './components/top-resources/top-resources.component';
import { WebinarsOnDemandLinkComponent } from './components/webinar-on-demand-link/webinars-on-demand-link.component';
import { WebinarsOnDemandCardComponent } from './components/webinars-on-demand-card/webinars-on-demand-card.component';
import { FavoriteResourcesComponent } from './components/favorite-resources/favorite-resources.component';
import { ExternalLinkModalComponent } from './components/external-link-modal/external-link-modal.component';
import { ExternalLinkDirective } from './directives/external-link.directive';
import { FooterComponent } from './components/footer/footer.component';

const isIE = window.navigator.userAgent.indexOf("MSIE ") > -1 || window.navigator.userAgent.indexOf("Trident/") > -1;

export function loggerCallback(logLevel: LogLevel, message: string) {
  //console.log(message);
}

export function MSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication({
    auth: {
      clientId: b2cPolicies.clientId, // Prod enviroment. Uncomment to use.
      authority: b2cPolicies.authorities.signUpSignIn.authority, // Defaults to "https://login.microsoftonline.com/common"
      knownAuthorities: [b2cPolicies.authorityDomain], // Mark your B2C tenant's domain as trusted.
      redirectUri: b2cPolicies.redirectUri,
      postLogoutRedirectUri: b2cPolicies.postLogoutRedirectUri,
      navigateToLoginRequestUrl: true
    },
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage,
      storeAuthStateInCookie: isIE, // set to true for IE 11      
    },
    system: {
      loggerOptions: {
        loggerCallback,
        logLevel: LogLevel.Info,
        piiLoggingEnabled: false
      }
    }
  });
}

export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const protectedResourceMap = new Map<string, Array<string>>();
  
  protectedResourceMap.set("https://localhost:4200/api/*", ['https://memicExtAuthDev.onmicrosoft.com/safetydirector/api/SafetyDirector.Read']);
  protectedResourceMap.set("https://safetydirector.azurewebsites.net/api/*", ['https://memicExtAuthDev.onmicrosoft.com/safetydirector/api/SafetyDirector.Read']);
  protectedResourceMap.set("https://memicapimanagenment.azure-api.net/safetydirector/api/*", ['https://memicExtAuthDev.onmicrosoft.com/safetydirector/api/SafetyDirector.Read']);

  protectedResourceMap.set("https://dev-angular-appserver-eus-01.azurewebsites.net/api/*", ['https://memicExtAuthDev.onmicrosoft.com/safetydirector/api/SafetyDirector.Read']);
  protectedResourceMap.set("https://dev-webapi-appserver-eus-01.azurewebsites.net/api/*", ['https://memicExtAuthDev.onmicrosoft.com/safetydirector/api/SafetyDirector.Read']);
  protectedResourceMap.set("https://dev-safetydirector-eus-01.azure-api.net/safetydirector/api/*", ['https://memicExtAuthDev.onmicrosoft.com/safetydirector/api/SafetyDirector.Read']);

  protectedResourceMap.set("https://qa-angular-appserver-eus-01.azurewebsites.net/api/*", ['https://memicExtAuthDev.onmicrosoft.com/safetydirector/api/SafetyDirector.Read']);
  protectedResourceMap.set("https://qa-webapi-appserver-eus-01.azurewebsites.net/api/*", ['https://memicExtAuthDev.onmicrosoft.com/safetydirector/api/SafetyDirector.Read']);
  protectedResourceMap.set("https://apim-qa-safetydirector-eus-01.azure-api.net/safetydirector/api/*", ['https://memicExtAuthDev.onmicrosoft.com/safetydirector/api/SafetyDirector.Read']);

  protectedResourceMap.set("https://uat-angular-appserver-eus-01.azurewebsites.net/api/*", ['https://memicExtAuthDev.onmicrosoft.com/safetydirector/api/SafetyDirector.Read']);
  protectedResourceMap.set("https://uat-webapi-appserver-eus-01.azurewebsites.net/api/*", ['https://memicExtAuthDev.onmicrosoft.com/safetydirector/api/SafetyDirector.Read']);
  protectedResourceMap.set("https://apim-uat-safetydirector-eus-01.azure-api.net/safetydirector/api/*", ['https://memicExtAuthDev.onmicrosoft.com/safetydirector/api/SafetyDirector.Read']);

  protectedResourceMap.set('https://graph.microsoft.com/v1.0/me', ['user.read']); // Prod environment. Uncomment to use
  protectedResourceMap.set('https://memicExtAuthDev.onmicrosoft.com/safetydirector/api', ['https://memicExtAuthDev.onmicrosoft.com/safetydirector/api/SafetyDirector.Read']);
  
  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap
  };
}

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    loginFailedRoute: '/login-failed'
  };
}

@NgModule({
  declarations: [
    AppComponent,
    BreadcrumbComponent,
    ToastsContainer,
    NavMenuComponent,
    NewsComponent,
    EventsComponent,
    PromotedLinksComponent,
    EErgoHomeComponent,
    EErgoDashboardComponent,
    OfficeErgonomicsComponent,
    ManualMaterialHandlingComponent,
    TopResourcesComponent,
    HomeComponent,
    ResourceLibraryComponent,
    FavoriteResourcesComponent,
    IndustryResourcePageComponent,
    IndustryHighlightsComponent,
    WebinarsOnDemandCardComponent,
    WebinarsOnDemandLinkComponent,
    DementiaSeriesComponent,
    MemicMinutesComponent,
    ExternalLinkModalComponent,
    ExternalLinkDirective,
    FooterComponent
  ],
  exports: [
    ExternalLinkDirective
  ],
  imports: [
    BrowserModule,
    HttpClientModule,
    ReactiveFormsModule,
    FormsModule,
    NgbCarouselModule,
    NgbPaginationModule,
    NgbToastModule,
    NgbDropdownModule,
    NgbModule,
    MsalModule,
    AppRoutingModule,
    SlickCarouselModule
  ],
  providers: [
    { provide: ErrorHandler, useClass: AppErrorHandler },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalInterceptor,
      multi: true
    },
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory
    },
    MsalService,
    MsalGuard,
    MsalBroadcastService
  ],
  bootstrap: [AppComponent, MsalRedirectComponent]
})
export class AppModule { }
